/**
 * Goal form advanced section — description, start value, deadline, reminder.
 */

import React from 'react'
import { useTranslation } from 'react-i18next'

interface GoalFormAdvancedProps {
  description: string
  startValue: string
  deadline: string
  monthlyReminder: boolean
  direction: 'increase' | 'decrease'
  onDescriptionChange: (v: string) => void
  onStartValueChange: (v: string) => void
  onDeadlineChange: (v: string) => void
  onReminderChange: (v: boolean) => void
}

export const GoalFormAdvanced: React.FC<GoalFormAdvancedProps> = ({
  description, startValue, deadline, monthlyReminder, direction,
  onDescriptionChange, onStartValueChange, onDeadlineChange, onReminderChange,
}) => {
  const { t } = useTranslation()

  return (
    <div className="space-y-4">
      {/* Description */}
      <div>
        <label className="text-xs font-medium text-text-hint mb-1.5 block">
          {t('goals.description')}
        </label>
        <textarea
          value={description}
          onChange={(e) => onDescriptionChange(e.target.value)}
          placeholder={t('goals.descriptionPlaceholder')}
          maxLength={500}
          rows={3}
          className="w-full px-3 py-2.5 rounded-xl bg-white/[0.06] border border-white/[0.08] text-sm text-text placeholder:text-text-hint/40 resize-none outline-none transition-all focus:ring-1 focus:ring-primary/20 focus:border-primary/50"
        />
        <span className="text-[10px] text-text-hint/50 mt-1 block text-right">
          {description.length}/500
        </span>
      </div>

      {/* Start value (only for decrease direction) */}
      {direction === 'decrease' && (
        <div>
          <label className="text-xs font-medium text-text-hint mb-1.5 block">
            {t('goals.startValue')}
          </label>
          <input
            type="number"
            inputMode="numeric"
            value={startValue}
            onChange={(e) => onStartValueChange(e.target.value)}
            placeholder="90"
            min={0}
            className="w-full h-10 px-3 rounded-xl bg-white/[0.06] border border-white/[0.08] text-sm text-text placeholder:text-text-hint/40 outline-none transition-all focus:ring-1 focus:ring-primary/20 focus:border-primary/50"
          />
        </div>
      )}

      {/* Deadline */}
      <div>
        <label className="text-xs font-medium text-text-hint mb-1.5 block">
          {t('goals.deadline')}
        </label>
        <input
          type="date"
          value={deadline}
          onChange={(e) => onDeadlineChange(e.target.value)}
          className="w-full h-10 px-3 rounded-xl bg-white/[0.06] border border-white/[0.08] text-sm text-text [color-scheme:dark] outline-none transition-all focus:ring-1 focus:ring-primary/20 focus:border-primary/50"
        />
      </div>

      {/* Monthly reminder toggle */}
      <button
        onClick={() => onReminderChange(!monthlyReminder)}
        className="w-full flex items-center justify-between py-2.5"
      >
        <span className="text-xs text-text">🔔 {t('goals.monthlyReminder')}</span>
        <div className={`w-9 h-5 rounded-full transition-colors relative ${monthlyReminder ? 'bg-primary' : 'bg-white/[0.12]'}`}>
          <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${monthlyReminder ? 'translate-x-4' : 'translate-x-0.5'}`} />
        </div>
      </button>
    </div>
  )
}
